import { useState } from 'react';
import { Switch } from '@headlessui/react';
import { Button } from '@app/components/Button/Button';
import {
  BusinessMemberPermissions,
  PermissionKey,
  PERMISSION_LABELS,
  PERMISSION_CATEGORIES,
} from '@app/models/business';
import UserProfileAddIcon from '@app/assets/icons/user-profile-add-01.svg?react';
import InfoIcon from '@app/assets/icons/info.svg?react';

export interface InviteMemberModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    email: string;
    jobTitle: string;
    permissions: BusinessMemberPermissions;
  }) => void;
  canInvite: boolean;
  existingEmails?: string[];
  isLoading?: boolean;
}

export const InviteMemberModal = ({
  open,
  onClose,
  onSubmit,
  canInvite,
  existingEmails = [],
  isLoading = false,
}: InviteMemberModalProps) => {
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [permissions, setPermissions] = useState<BusinessMemberPermissions>({});
  const [emailError, setEmailError] = useState('');

  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailError('');
  };

  const handleTogglePermission = (key: PermissionKey) => {
    setPermissions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const resetForm = () => {
    setEmail('');
    setJobTitle('');
    setPermissions({});
    setEmailError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }
    if (!validateEmail(email.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }
    const normalizedEmail = email.trim().toLowerCase();
    const isDuplicate = existingEmails.some(
      (existingEmail) => existingEmail?.toLowerCase() === normalizedEmail
    );
    if (isDuplicate) {
      setEmailError('This email is already a member of this business');
      return;
    }

    onSubmit({
      email: email.trim(),
      jobTitle: jobTitle.trim(),
      permissions,
    });
    resetForm();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-[744px] bg-white max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-base-ghostWhite flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-base-ghostWhite rounded-lg flex items-center justify-center flex-shrink-0">
                <UserProfileAddIcon className="w-5 h-5 text-base-coolGray" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-base-raisinBlack">Invite new member</h3>
                <p className="text-sm text-base-coolGray mt-1">
                  Fill in the information below to invite a new team member
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-base-coolGray hover:text-base-raisinBlack"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-6 mb-6 border-b border-base-ghostWhite -mx-6 px-6">
            <div>
              <label className="block text-sm font-medium text-base-raisinBlack mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder="Email"
                className={`w-full px-4 py-3 bg-base-ghostWhite border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-baseBlue focus:bg-white ${
                  emailError ? 'border-red-500' : 'border-transparent'
                }`}
              />
              {emailError && (
                <p className="text-xs text-red-500 mt-1">{emailError}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-base-raisinBlack mb-2">
                Job title  <span className="text-base-coolGray font-normal">(Optional)</span>
              </label>
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Job title"
                className="w-full px-4 py-3 bg-base-ghostWhite border border-transparent rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-baseBlue focus:bg-white"
              />
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-base-raisinBlack mb-1">Permissions</h4>
            <p className="text-sm text-base-coolGray mb-6">
              Select the areas of the platform this member can edit.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <h5 className="text-xs font-semibold text-base-coolGray pb-3 mb-3 uppercase tracking-wider border-b border-base-ghostWhite">
                  Reviews
                </h5>
                <div className="space-y-3">
                  <div>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <Switch
                        checked={permissions.respondToReviews === true}
                        onChange={() => handleTogglePermission('respondToReviews')}
                        className="group relative flex h-6 w-12 cursor-pointer rounded-full bg-gray-200 p-1 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-brand-baseBlue flex-shrink-0"
                      >
                        <span
                          aria-hidden="true"
                          className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
                        />
                      </Switch>
                      <span className="text-sm text-base-raisinBlack">
                        {PERMISSION_LABELS.respondToReviews}
                      </span>
                    </label>
                    <div className="flex items-start gap-2 mt-2">
                      <InfoIcon className="w-4 h-4 text-brand-baseBlue flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-base-coolGray">
                        Only members who are connected to your business review platforms as members can respond to reviews.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="text-xs font-semibold text-base-coolGray pb-3 mb-3 uppercase tracking-wider border-b border-base-ghostWhite">
                  Customers
                </h5>
                <div className="space-y-3">
                  {PERMISSION_CATEGORIES.CUSTOMERS.permissions.map((permKey) => (
                    <label
                      key={permKey}
                      className="flex items-center gap-3 cursor-pointer"
                    >
                      <Switch
                        checked={permissions[permKey] === true}
                        onChange={() => handleTogglePermission(permKey)}
                        className="group relative flex h-6 w-12 cursor-pointer rounded-full bg-gray-200 p-1 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-brand-baseBlue flex-shrink-0"
                      >
                        <span
                          aria-hidden="true"
                          className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
                        />
                      </Switch>
                      <span className="text-sm text-base-raisinBlack">
                        {PERMISSION_LABELS[permKey]}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h5 className="text-xs font-semibold text-base-coolGray pb-3 mb-3 uppercase tracking-wider border-b border-base-ghostWhite">
                  Campaigns & Automations
                </h5>
                <div className="space-y-3">
                  {PERMISSION_CATEGORIES.CAMPAIGNS_AUTOMATIONS.permissions.map((permKey) => (
                    <label
                      key={permKey}
                      className="flex items-center gap-3 cursor-pointer"
                    >
                      <Switch
                        checked={permissions[permKey] === true}
                        onChange={() => handleTogglePermission(permKey)}
                        className="group relative flex h-6 w-12 cursor-pointer rounded-full bg-gray-200 p-1 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-brand-baseBlue flex-shrink-0"
                      >
                        <span
                          aria-hidden="true"
                          className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
                        />
                      </Switch>
                      <span className="text-sm text-base-raisinBlack">
                        {PERMISSION_LABELS[permKey]}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h5 className="text-xs font-semibold text-base-coolGray pb-3 mb-3 uppercase tracking-wider border-b border-base-ghostWhite">
                  Global
                </h5>
                <div className="space-y-3">
                  {PERMISSION_CATEGORIES.GLOBAL.permissions.map((permKey) => (
                    <label
                      key={permKey}
                      className="flex items-center gap-3 cursor-pointer"
                    >
                      <Switch
                        checked={permissions[permKey] === true}
                        onChange={() => handleTogglePermission(permKey)}
                        className="group relative flex h-6 w-12 cursor-pointer rounded-full bg-gray-200 p-1 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-brand-baseBlue flex-shrink-0"
                      >
                        <span
                          aria-hidden="true"
                          className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
                        />
                      </Switch>
                      <span className="text-sm text-base-raisinBlack">
                        {PERMISSION_LABELS[permKey]}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-base-ghostWhite flex-shrink-0">
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 px-6 py-3 bg-base-ghostWhite text-base-raisinBlack font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <Button
              onClick={handleSubmit}
              disabled={!canInvite || isLoading}
              className="flex-1"
            >
              {isLoading ? 'Sending...' : 'Send an invitation by email'}
            </Button>
          </div>
          {!canInvite && (
            <p className="text-xs text-red-500 text-center mt-3">
              No seats available. Upgrade your plan to invite more members.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
